import {
  alpha,
  Autocomplete,
  Box,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScanConsultantReviewDTO from '@src/adapters/dto/scan/ScaneConsultantReviewDTO';
import ScanProcedureDTO from '@src/adapters/dto/scan/ScanProcedureDTO';
import { useMemo } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';

interface Props {
  selectedProc: ScanProcedureDTO | undefined;
  selectedItem: ScanConsultantReviewDTO | undefined;
  procedures: ScanProcedureDTO[];
  selectedIndex: number;
  onMoveScan(forward: boolean): void;
  onSelectScan(scan: ScanConsultantReviewDTO): void;
  onSelectProcedure(proc: ScanProcedureDTO): void;
  onMigrate(migrate: boolean): void;
  onReassign(): void;
}

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

function Header({
  selectedProc,
  selectedItem,
  procedures,
  selectedIndex,
  onMoveScan,
  onSelectScan,
  onSelectProcedure,
  onMigrate,
  onReassign,
}: Props) {
  const getIcon = (scan: ScanConsultantReviewDTO | undefined) => {
    if (scan) {
      if (scan.isDoNotMigrate) return <HighlightOffIcon color="error" />;
      if (scan.isExtraFile) return <AttachFileIcon color="info" />;
      if (scan.isMigrate) return <CheckCircleOutlineIcon color="success" />;
    } else {
      return '';
    }
  };
  const indexedScans = useMemo(() => {
    return procedures.flatMap((procedure) => procedure.scans);
  }, [procedures]);

  const disableMoveNext = useMemo(() => {
    let lastIndex = 0;

    if (selectedProc) {
      if (selectedProc.id === ZERO_GUID) {
        lastIndex = indexedScans.length - 1;
      } else {
        lastIndex = indexedScans
          .map((item) => item.procedure?.id)
          .lastIndexOf(selectedProc.id);
      }
    }

    return lastIndex === selectedIndex;
  }, [indexedScans, selectedIndex, selectedProc]);

  const disableMovePrevious = useMemo(() => {
    let firstIndex = 0;
    if (selectedProc) {
      if (selectedProc.id === ZERO_GUID) {
        firstIndex = indexedScans.findIndex((ia) => !ia.procedure);
      } else {
        firstIndex = indexedScans.findIndex(
          (ia) => ia.procedure?.id === selectedProc.id
        );
      }
    }

    return firstIndex === selectedIndex;
  }, [indexedScans, selectedIndex, selectedProc]);

  return (
    <>
      {selectedProc && selectedItem && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            backgroundColor: '#fff',
            padding: theme.spacing(2),
            height: theme.spacing(8.5),
            width: '100%',
          })}>
          <Box
            sx={(theme) => ({
              width: '70%',
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
            })}>
            <Autocomplete
              disableClearable
              selectOnFocus
              size="small"
              onChange={(event, proc) => onSelectProcedure(proc)}
              style={{ minWidth: '200px', width: '30%' }}
              isOptionEqualToValue={(
                option: ScanProcedureDTO,
                value: ScanProcedureDTO
              ) => option?.id === value?.id}
              value={selectedProc}
              options={procedures}
              getOptionLabel={(proc) => {
                const numberOfScans = proc?.scans.length || 0;
                return `${proc.name} (${numberOfScans})`;
              }}
              renderOption={(props, option) => {
                const numberOfScans = option?.scans.length || 0;
                const complete =
                  option?.scans.length ===
                  option?.scans.filter(
                    (p) => p.isDoNotMigrate || p.isMigrate || p.isExtraFile
                  ).length;
                const isDoNotMigrate = option?.scans.find(
                  (a) => a.isDoNotMigrate
                );
                return (
                  <ListItem {...props} key={option.id}>
                    <ListItemIcon>
                      {complete ? (
                        isDoNotMigrate ? (
                          <HighlightOffIcon color="error" />
                        ) : (
                          <CheckCircleOutlineIcon color="success" />
                        )
                      ) : (
                        ''
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${option.name} (${numberOfScans})`}
                    />
                  </ListItem>
                );
              }}
              renderInput={(params) => {
                const complete =
                  selectedProc?.scans.length ===
                  selectedProc?.scans.filter(
                    (p) => p.isDoNotMigrate || p.isMigrate || p.isExtraFile
                  ).length;
                const isDoNotMigrate = selectedProc?.scans.find(
                  (a) => a.isDoNotMigrate
                );
                return (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <span
                            style={{
                              fontSize: '0.9em',
                              display: 'flex',
                            }}>
                            {complete ? (
                              isDoNotMigrate ? (
                                <HighlightOffIcon color="error" />
                              ) : (
                                <CheckCircleOutlineIcon color="success" />
                              )
                            ) : (
                              ''
                            )}
                          </span>
                        </>
                      ),
                    }}
                  />
                );
              }}
              sx={{ paddingRight: (theme) => theme.spacing(2) }}
            />
            <Autocomplete
              disableClearable
              selectOnFocus
              size="small"
              onChange={(event, scan) => onSelectScan(scan)}
              style={{ minWidth: '200px', width: '30%' }}
              isOptionEqualToValue={(
                option: ScanConsultantReviewDTO,
                value: ScanConsultantReviewDTO
              ) => option?.id === value?.id}
              value={selectedItem}
              options={selectedProc.scans}
              getOptionLabel={(option) => {
                return option?.originalFilename || '';
              }}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.id}>
                  <ListItemIcon>{getIcon(option)}</ListItemIcon>
                  <ListItemText primary={option.originalFilename} />
                </ListItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedItem && getIcon(selectedItem)}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                width: '40%',
                gap: theme.spacing(1),
              })}>
              <IconButton
                aria-label="back"
                size="small"
                onClick={() => onMoveScan(false)}
                disabled={disableMovePrevious}
                sx={(theme) => ({
                  backgroundColor: alpha(theme.palette.primary.main, 0.9),
                  color: 'white',
                  '&.Mui-disabled': {
                    color: 'rgba(157, 165, 186, 0.26)',
                    backgroundColor: 'rgba(157, 165, 186, 0.12)',
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                })}>
                <ArrowBackIcon fontSize="inherit" />
              </IconButton>
              <IconButton
                aria-label="forward"
                size="small"
                onClick={() => onMoveScan(true)}
                disabled={disableMoveNext}
                sx={(theme) => ({
                  backgroundColor: alpha(theme.palette.primary.main, 0.9),
                  color: 'white',
                  '&.Mui-disabled': {
                    color: 'rgba(157, 165, 186, 0.26)',
                    backgroundColor: 'rgba(157, 165, 186, 0.12)',
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                })}>
                <ArrowForwardIcon fontSize="inherit" />
              </IconButton>
              <Button
                variant={selectedItem.isMigrate ? 'contained' : 'outlined'}
                size="medium"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                sx={{ minWidth: '142px' }}
                onClick={() => onMigrate(true)}>
                {selectedItem.isMigrate ? 'Selected' : 'Select'}
              </Button>
            </Box>
          </Box>
          <Box
            sx={(theme) => ({
              width: '30%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing(1),
            })}>
            <Button
              variant="outlined"
              size="medium"
              color="info"
              onClick={() => onReassign()}>
              Reassign
            </Button>
            <Button
              variant={selectedItem.isDoNotMigrate ? 'contained' : 'outlined'}
              size="medium"
              startIcon={<HighlightOffIcon />}
              color="error"
              onClick={() => onMigrate(false)}>
              Do Not Migrate
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}

export default Header;
